<template>
  <div class="ws-Step">
    <AppIcon height="104" icon-name="onboarding-cat" width="68" />

    <div class="ws-Step_Content">
      <slot name="step-title" />

      <i18n-t :keypath="keyPath" class="ws-Step_Subtitle" scope="global" tag="div">
        <template #break>
          <br />
        </template>
      </i18n-t>

      <template v-if="setupNameStepNeed">
        <AppDivider no-margin />

        <FormFieldNext
          :label="$t('onboarding.setup_name.input_label')"
          class="ws-Step_Field"
          required
        >
          <AppInput
            v-model="name"
            :is-error="!isNameValid"
            :max-length="50"
            :placeholder="$t('onboarding.setup_name.input_label')"
            blur-on-enter
            size="xlg"
            style-type="primary"
            @focus="isNameValid = true"
          />
          <AppFieldError v-if="!isNameValid" :show="!isNameValid">
            {{ $t('field.required') }}
          </AppFieldError>
        </FormFieldNext>
      </template>

      <template v-if="!isRerunOnboarding">
        <AppCheckbox
          v-if="isMondayExternalPlatform"
          v-model="localCookiesAccepted"
          :class="{
            'ws-Step_Checkbox-error': showCookieError
          }"
          align-start
          class="ws-Step_Checkbox"
        >
          {{ $t('onboarding.cookies') }}
          <AppButton
            :size="BUTTON_SIZES.XS"
            class="ws-Step_CheckboxContentButton"
            remove-padding
            type="link-next"
            @click="onLearnMore"
          >
            {{ $t('action.learn_more') }}
          </AppButton>

          <AppFieldError
            v-if="showCookieError"
            :show="showCookieError"
            class="ws-Step_CheckboxContentErrorMessage"
          >
            {{ $t('field.check_above_to_proceed') }}
          </AppFieldError>
        </AppCheckbox>

        <AppCheckbox v-model="localSubscription" align-start class="ws-Step_Checkbox">
          {{ $t('onboarding.subscription') }}
        </AppCheckbox>
      </template>

      <template v-if="setupNameStepNeed">
        <AppDivider no-margin />

        <AppButton
          :loading="loading"
          class="ws-Step_Action"
          icon-after="chevron-right-next"
          type="primary-next"
          @click="onContinue"
        >
          {{ $t('action.lets_start') }}
        </AppButton>
      </template>
    </div>

    <portal to="modal-windows">
      <CookieInfoModal
        :hide-confirm-button="cookiesAccepted"
        :show="isCookieInfoShow"
        @on-close="hideCookieInfoModal"
        @on-confirm="onConfirmCookie"
      />
    </portal>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'

import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import { handleError } from '@/utils/error-handling'
import { useRenameUser, useSetSubscriptionsForUser } from '@/utils/web-app/composables'

import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInput from '@/components/ui/AppInput/AppInput'
import CookieInfoModal from '@/components/ui/onboarding/CookieInfoModal'

defineOptions({
  name: 'WelcomeSetupNameStep'
})

const props = defineProps({
  subscription: {
    type: Boolean
  },

  cookiesAccepted: {
    type: Boolean
  },

  showCookieError: {
    type: Boolean
  },

  isRerunOnboarding: {
    type: Boolean
  },

  setupNameStepNeed: {
    type: Boolean
  },

  isMondayExternalPlatform: {
    type: Boolean
  }
})

const keyPath = computed(() => {
  return props.isRerunOnboarding
    ? 'onboarding.welcome.rerun_subtitle'
    : 'onboarding.welcome.subtitle'
})

const emit = defineEmits({
  'update:subscription': null,
  'update:cookies-accepted': null,
  'update:show-cookie-error': null,
  continue: null
})

const localSubscription = computed({
  get: () => props.subscription,
  set: value => {
    emit('update:subscription', value)
  }
})

const { setSubscriptionsForUser } = useSetSubscriptionsForUser({
  localSubscription
})

onBeforeUnmount(() => {
  if (localSubscription.value && !props.isRerunOnboarding) {
    setSubscriptionsForUser()
  }
})

const { onRenameUser, name, loading, isNameValid } = useRenameUser()
const onContinue = async () => {
  try {
    await onRenameUser()
    if (!isNameValid.value) return
    emit('continue')
  } catch (error) {
    handleError({ error })
  }
}

const localCookiesAccepted = computed({
  get: () => props.cookiesAccepted,
  set: value => {
    emit('update:cookies-accepted', value)
    emit('update:show-cookie-error', false)
  }
})

const isCookieInfoShow = ref(false)

const hideCookieInfoModal = () => {
  isCookieInfoShow.value = false
}

const onConfirmCookie = () => {
  localCookiesAccepted.value = true
  hideCookieInfoModal()
}

const onLearnMore = () => {
  isCookieInfoShow.value = true
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/wizard-steps';

.ws-Step {
  @extend %wizard-step-styles;

  display: grid;
  grid-template-columns: 1fr var(--step-max-width) 1fr;
  --step-gap: 50px;
  width: fit-content;
}

.ws-Step_Checkbox {
  // max-width: 496px;
  --font-size: #{$fs-12};
  --line-height: 16px;
  --gap: 12px;
  padding-left: 4px;

  &-error {
    --checkmark-border-color: #{$grade-low-color-next};
  }
}

.ws-Step_CheckboxContentButton {
  font-size: #{$fs-12};
  line-height: 16px;
  display: inline-block;
}

.ws-Step_CheckboxContentErrorMessage {
  display: block;
  margin-top: 8px;
}

.ws-Step_Content {
  @extend %wizard-step-content-styles;
}

.ws-Step_Subtitle {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 20px;
  font-weight: fw('semi-bold');
}

// .ws-Step_Divider {
//   width: 100%;
// }

.ws-Step_Field {
  width: 100%;
}

.ws-Step_Action {
  --button-low-padding: 4px;

  margin-left: auto;
}
</style>
